import React, { useMemo, useState } from "react";
import emailjs from "emailjs-com";
import { Helmet } from "react-helmet-async";
import {
  Phone,
  Mail,
  MapPin,
  Send,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
} from "lucide-react";

type Status = "idle" | "success" | "error";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    service: "General Inquiry",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<Status>("idle");

  const canSubmit = useMemo(() => {
    return (
      formData.name.trim().length > 1 &&
      formData.email.trim().includes("@") &&
      formData.message.trim().length > 10 &&
      !isSubmitting
    );
  }, [formData, isSubmitting]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus("idle");

    try {
      const result = await emailjs.send(
        "service_r3979w7",
        "template_k64x2ck",
        {
          from_name: formData.name,
          from_email: formData.email,
          subject: formData.subject || `${formData.service} Inquiry`,
          service: formData.service,
          message: formData.message,
        },
        "E93GaHcHRVcxUn8ix"
      );

      console.log("Email sent:", result.text);
      setStatus("success");
      setFormData({
        name: "",
        email: "",
        subject: "",
        service: "General Inquiry",
        message: "",
      });
    } catch (error) {
      console.error("Email error:", error);
      setStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Call Us",
      value: "+254 700041385",
      helper: "Mon–Fri, 8AM–6PM EAT",
      href: "tel:+254700041385",
    },
    {
      icon: Mail,
      title: "Email Us",
      value: "info@northstartsystems.com",
      helper: "Reply within 24 hours",
      href: "mailto:info@northstartsystems.com",
    },
    {
      icon: MapPin,
      title: "Visit Us",
      value:
        "Riara Valley Garden, Apt C8, 3rd Floor, Off Riara Road, Kilimani, Nairobi",
      helper: "East Africa Hub",
      href: "https://maps.google.com/?q=Riara%20Valley%20Garden%20Kilimani%20Nairobi",
    },
  ];

  return (
    <section
      id="contact"
      className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-indigo-50 py-20"
    >
      <Helmet>
        <title>Contact Us | Northstart Systems</title>
        <meta
          name="description"
          content="Get in touch with Northstart Systems. Call, email, or visit our Nairobi office. We help with ICT, procurement, and security solutions."
        />
        <link rel="canonical" href="https://northstartsystems.com/contact" />
      </Helmet>

      {/* Soft background accents (cleaner + more human) */}
      <div className="pointer-events-none absolute -top-28 left-1/4 h-80 w-80 rounded-full bg-blue-200/35 blur-3xl" />
      <div className="pointer-events-none absolute top-28 right-1/4 h-96 w-96 rounded-full bg-indigo-200/30 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-4">
        {/* Header */}
        <div className="mx-auto mb-14 max-w-4xl text-center">
          <h2 className="mt-6 text-4xl font-extrabold tracking-tight text-slate-900 md:text-5xl">
            Get in{" "}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Touch
            </span>
          </h2>

          <p className="mt-4 text-lg leading-relaxed text-slate-600 md:text-xl">
            Share what you need ICT, procurement systems, web solutions, or security.
            We’ll respond with a clear next step.
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-2 lg:gap-12">
          {/* Contact Information */}
          <aside className="space-y-6" aria-label="Contact Information">
            <div className="rounded-3xl border border-slate-200 bg-white/80 p-8 shadow-sm backdrop-blur">
              <div className="flex items-center gap-4">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-600 text-white shadow-sm">
                  <Sparkles className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="text-2xl font-extrabold text-slate-900">Connect With Us</h3>
                  <p className="mt-1 text-slate-900 font-bold">Choose what’s easiest for you.</p>
                </div>
              </div>

              <ul className="mt-8 space-y-4">
                {contactInfo.map((info, index) => {
                  const Icon = info.icon;
                  return (
                    <li
                      key={index}
                      className="rounded-2xl border border-slate-800 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                    >
                      <div className="flex items-start gap-4">
                        <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-200 text-indigo-700 ring-1 ring-indigo-100">
                          <Icon className="h-5 w-5" />
                        </span>

                        <div className="flex-1">
                          <div className="flex items-center justify-between gap-3">
                            <h4 className="text-base font-extrabold text-slate-900">{info.title}</h4>
                            <a
                              href={info.href}
                              target={info.title === "Visit Us" ? "_blank" : undefined}
                              rel={info.title === "Visit Us" ? "noopener noreferrer" : undefined}
                              className="text-sm font-semibold text-indigo-700 hover:text-indigo-800"
                            >
                              Open
                            </a>
                          </div>

                          <p className="mt-1 text-sm font-semibold text-slate-700">{info.value}</p>
                          <p className="mt-1 text-sm text-slate-500">{info.helper}</p>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>

              {/* Business Hours */}
              <div className="mt-6 rounded-2xl border border-indigo-300 bg-indigo-50/60 p-5">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-indigo-700" />
                  <h4 className="text-xl font-extrabold text-slate-900">Business Hours</h4>
                </div>
                <div className="mt-3 space-y-1 text-sm text-slate-600">
                  <p>
                    <span className="font-semibold">Mon–Fri:</span> 8:00 AM – 6:00 PM (EAT)
                  </p>
                  <p>
                    <span className="font-semibold">Sat:</span> 9:00 AM – 1:00 PM (EAT)
                  </p>
                  <p>
                    <span className="font-semibold">Sun:</span> Emergency Support Only
                  </p>
                </div>
              </div>
            </div>
          </aside>

          {/* Contact Form */}
          <article className="rounded-3xl border border-slate-200 bg-white/90 p-8 shadow-sm backdrop-blur">
            <div className="flex items-center gap-4">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-600 text-white shadow-sm">
                <Send className="h-5 w-5" />
              </span>
              <div>
                <h3 className="text-2xl font-extrabold text-slate-900">Send us a Message</h3>
                <p className="mt-1 text-slate-600">We’ll respond within 24 hours.</p>
              </div>
            </div>

            {/* Inline status (more modern than alert()) */}
            {status !== "idle" && (
              <div
                className={`mt-6 rounded-2xl border p-4 text-sm ${
                  status === "success"
                    ? "border-emerald-400 bg-emerald-200 text-emerald-800"
                    : "border-rose-400 bg-rose-200 text-rose-800"
                }`}
                role="status"
              >
                <div className="flex items-start gap-2">
                  {status === "success" ? (
                    <CheckCircle2 className="mt-0.5 h-5 w-5" />
                  ) : (
                    <AlertTriangle className="mt-0.5 h-5 w-5" />
                  )}
                  <div>
                    <p className="font-bold">
                      {status === "success" ? "Message sent!" : "Something went wrong"}
                    </p>
                    <p className="mt-1">
                      {status === "success"
                        ? "Thanks — we’ll get back to you shortly."
                        : "Please try again, or email us directly at info@northstartsystems.com."}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              <div className="grid gap-5 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-xl font-bold text-slate-700">Full Name</label>
                  <input
                    type="text"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xl font-bold text-slate-700">Email Address</label>
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
                    required
                  />
                </div>
              </div>

              {/* Service + Subject = higher quality leads */}
              <div className="grid gap-5 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-xl font-bold text-slate-700">Service</label>
                  <select
                    value={formData.service}
                    onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                    className="w-full rounded-2xl border border-slate-800 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
                  >
                    <option>General Inquiry</option>
                    <option>ICT Systems & Support</option>
                    <option>Security Solutions</option>
                    <option>E-Procurement Solutions</option>
                    <option>Development Solutions</option>
                    <option>Consultancy</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-xl font-bold text-slate-700">Subject (optional)</label>
                  <input
                    type="text"
                    placeholder="e.g., CCTV installation for office"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full rounded-2xl border border-slate-800 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xl font-bold text-slate-700">Message</label>
                <textarea
                  placeholder="Tell us what you’re trying to achieve, your timeline, and any constraints..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="h-32 w-full resize-none rounded-2xl border border-slate-800 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
                  required
                />
                <p className="text-m italic font-bold text-slate-900">
                  Tip: include location + timeline for a faster response.
                </p>
              </div>

              <button
                type="submit"
                disabled={!canSubmit}
                className="group inline-flex w-full items-center justify-center rounded-2xl bg-indigo-800 px-6 py-4 text-xl font-extrabold text-cyan-500 shadow-lg shadow-indigo-600/20 transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <span className="mr-2 h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    Sending…
                  </>
                ) : (
                  <>
                    Send Message
                    <Send className="ml-2 h-5 w-5 transition group-hover:translate-x-0.5" />
                  </>
                )}
              </button>

              {/* Trust / reassurance */}
              <div className="mt-4 flex flex-wrap items-center justify-center gap-6 text-xs font-semibold text-slate-500">
                <span className="inline-flex items-center gap-2 rounded-full bg-slate-200 px-3 py-2">
                  <CheckCircle2 className="h-6 w-6 text-indigo-600 text-xl font-bold" /> Secure submission
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-slate-200 px-3 py-2">
                  <CheckCircle2 className="h-6 w-6 text-indigo-600 text-xl font-bold" /> Response within 24h
                </span>
              </div>
            </form>
          </article>
        </div>
      </div>
    </section>
  );
};

export default Contact;